import { Typography } from '@mui/material';
import { AdjustmentTableItem, AdjustmentType, ImportAdjustmentsStructure } from '../../models/adjustment.types';
import { ButtonOne } from '../ButtonOne/ButtonOne.component';
import { SelectableTable } from '../SelectableTable/SelectableTable.component';
import { SelectableTableColumn } from '../SelectableTable/SelectableTable.types';
import { EmptyAdjustments } from './EmptyAdjustments.component';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useEffect, useState } from 'react';
import { ImportAdjustments } from './ImportAdjustments.component';
import { Prompt } from '../Prompt/Prompt.component';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import { useTranslation } from 'react-i18next';

export const AdjustmentsTable: React.FC<{
  type: AdjustmentType;
  items: AdjustmentTableItem[];
  columns: SelectableTableColumn[];
  onImportClick: () => void;
  closeImport: boolean;
  isConfigured?: boolean;
  onValidFileSelected: (parsedAdjustmentsStructure?: ImportAdjustmentsStructure) => void;
  importedFromExternalSystem: boolean;
  onImportFromExternalSystem: () => void;
  selectedSystem?: string;
}> = (props) => {
  const [importOpen, setImportOpen] = useState<boolean>(false);
  const [confirmImportOpen, setConfirmImportOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const hasImportPermission = usePermission(UserPermission.AdminSessionStatusManage);

  const openImport = () => {
    setImportOpen(true);
    props.onImportClick();
  };

  useEffect(() => {
    if (props.items.length === 0 && hasImportPermission) {
      openImport();
    } else setImportOpen(false);
  }, [props.type, props.items]);

  useEffect(() => {
    if (importOpen && props.closeImport) setImportOpen(false);
  }, [props.closeImport]);

  const getTable = () => {
    return props.items.length > 0 ? (
      <div style={{ height: '450px', overflow: 'auto' }}>
        <SelectableTable<AdjustmentTableItem>
          columns={props.columns}
          rows={props.items}
          oddTableDoesNotChangeColor
          caption='Lista dostosowań'
        />
      </div>
    ) : (
      <EmptyAdjustments type={props.type} />
    );
  };

  const labels =
    props.type === AdjustmentType.means
      ? { header: 'Sposoby dostosowań', button: 'DOSTOSOWAŃ', promptMain: 'dostosowania', promptSub: 'dostosowań' }
      : { header: 'Uprawnienia do dostosowań', button: 'UPRAWNIEŃ', promptMain: 'uprawnienia', promptSub: 'uprawnień' };

  return (
    <div
      style={{
        width: '100%',
        minHeight: '450px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px' }}>
        <Typography>{labels.header}</Typography>
        {hasImportPermission && !importOpen && (
          <ButtonOne
            variant="contained"
            color="primary"
            startIcon={<UploadFileIcon />}
            onClick={() => {
              if (props.items.length > 0) {
                setConfirmImportOpen(true);
              } else openImport();
            }}
            disabled={props.isConfigured}
          >{`IMPORT ${labels.button}`}</ButtonOne>
        )}
      </div>
      {importOpen ? (
        <ImportAdjustments
          type={props.type}
          isConfigured={props.isConfigured}
          onValidFileSelected={props.onValidFileSelected}
          importedFromExternalSystem={props.importedFromExternalSystem}
          onImportFromExternalSystem={props.onImportFromExternalSystem}
          selectedSystem={props.selectedSystem}
        />
      ) : (
        getTable()
      )}
      <Prompt
        open={confirmImportOpen}
        type={'confirmation'}
        mainText={`Ponowny import danych usunie poprzednio wprowadzone ${labels.promptMain}.`}
        subTexts={[
          {
            text: `Czy chcesz przejść do okna importu ${labels.promptSub}?`,
          },
        ]}
        mainActionButtonText="TAK"
        onConfirm={() => {
          setConfirmImportOpen(false);
          openImport();
        }}
        onCancel={() => setConfirmImportOpen(false)}
      />
    </div>
  );
};
